import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency as baseFmtCurrency } from '../utils/formatters';

/**
 * Hook that returns currency-aware formatting functions bound to the
 * active company's currency settings (symbol AND amount decimal places).
 *
 * Usage:
 *   const { formatCurrency, decimalPlaces, currencyCode, currencySymbol } = useCurrency();
 *   formatCurrency(50000)  // "Rs.50,000.00" or "AED50,000" depending on company setting
 */
const useCurrency = () => {
  const { activeCompany } = useAuth();
  const currencyCode = activeCompany?.currencyCode || 'PKR';
  const currencySymbol = activeCompany?.currencySymbol || 'Rs.';
  // Honour both the flat (canonical) and nested (legacy) storage paths.
  const dpRaw = activeCompany?.decimalPlaces ?? activeCompany?.currency?.decimalPlaces;
  const decimalPlaces = Number.isFinite(Number(dpRaw)) ? Number(dpRaw) : 2;

  const formatCurrency = useMemo(() => {
    return (value, options = {}) => baseFmtCurrency(value, { symbol: currencySymbol, decimals: decimalPlaces, ...options });
  }, [currencySymbol, decimalPlaces]);

  return { formatCurrency, decimalPlaces, currencyCode, currencySymbol };
};

export default useCurrency;
